import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  InputAdornment,
  useTheme,
} from '@mui/material';
import {
  StyledTableCellWithoutLeft,
  StyledTableCellWithoutLeftRight,
} from 'routes/Main/style';
import Form from 'components/Form';

import useTableForm from '../../hooks/useTableForm';
import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

import { ReactComponent as MMSE9pic } from 'assets/MMSE_9_pic.svg';

const contentLabel = [
  {
    id: 1,
    ko: 'Category',
    desc: [
      '1. 每个正确答案得 1 分',
      '2. 每个正确答案得 1 分',
      '3. 我会说三个词/物品，你需要重复它们。然后记住这些词，因为稍后我会让你再说一遍它们。',
    ],
    scores: [5, 5, 3],
    ids: [0, 1, 2],
  },
  {
    id: 2,
    ko: 'Registration',
    desc: ['4. 倒着拼写“WORLD”'],
    scores: [5],
    ids: [3],
  },
  {
    id: 3,
    ko: 'Attention',
    desc: ['5."现在说出我让你记住的三个物品/词语。" (每个正确答案得 1 分。)"'],
    scores: [3],
    ids: [4],
  },
  {
    id: 4,
    ko: 'Language',
    desc: [
      '6. "这是什么物品？" 展示一块手表。',
      '7. "这是什么物品？" 展示一支铅笔。',
      '8. "重复这句话：没有如果，没有但是。"',
      '9. "读这些词，然后按照指示去做。" (给患者一张写有“闭上眼睛”的纸。)',
    ],
    scores: [1, 1, 1, 1],
    ids: [5, 6, 7, 8],
  },
  {
    id: 5,
    ko: 'Recall',
    desc: ['10. "用你的右手/左手拿起这张纸，把它对折，然后放在地上。"'],
    scores: [3],
    ids: [9],
  },
  {
    id: 6,
    ko: 'Understanding\n/Judgement',
    desc: [
      '11. “编写并在纸上写出一个完整的句子。" (句子必须包含一个动词和一个名词。)',
      '12. “请复制这个图案。"',
    ],
    scores: [1, 1],
    ids: [10, 11],
  },
];

const questions1 = [
  '“今年是哪一年？”',
  '“现在是什么季节？”',
  '“现在是几月？”',
  '“今天的日期是什么？”',
  '“今天是星期几？”',
];

const questions2 = [
  '“我们现在在哪个国家？”',
  '“我们在哪个州/省？”',
  '“我们在哪个城市或镇？”',
  '“你的家庭地址是什么？/这栋建筑的名称是什么？”',
  '“我们在哪一层楼？/我们现在在哪个房间？”',
];

const scoreLabel = [
  { score: '30-26', label: '正常 (可能正常)' },
  { score: '25-20', label: '轻度 (早期)' },
  { score: '19-10', label: '中度 (中期)' },
  { score: '9-0', label: '重度 (晚期)' },
];

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
}

const MMSEContents = (props: Props) => {
  const { palette } = useTheme();
  const { disabled, getValues, register } = props;

  const min = 0;
  const max = 5;

  const [input1, setInput1] = useState(
    getValues('score[0]') ? Number(getValues('score[0]')) : 0
  );
  const [input2, setInput2] = useState(
    getValues('score[1]') ? Number(getValues('score[1]')) : 0
  );
  const [input3, setInput3] = useState(
    getValues('score[2]') ? Number(getValues('score[2]')) : 0
  );
  const [input4, setInput4] = useState(
    getValues('score[3]') ? Number(getValues('score[3]')) : 0
  );
  const [input5, setInput5] = useState(
    getValues('score[4]') ? Number(getValues('score[4]')) : 0
  );
  const [input6, setInput6] = useState(
    getValues('score[5]') ? Number(getValues('score[5]')) : 0
  );
  const [input7, setInput7] = useState(
    getValues('score[6]') ? Number(getValues('score[6]')) : 0
  );
  const [input8, setInput8] = useState(
    getValues('score[7]') ? Number(getValues('score[7]')) : 0
  );
  const [input9, setInput9] = useState(
    getValues('score[8]') ? Number(getValues('score[8]')) : 0
  );
  const [input10, setInput10] = useState(
    getValues('score[9]') ? Number(getValues('score[9]')) : 0
  );
  const [input11, setInput11] = useState(
    getValues('score[10]') ? Number(getValues('score[10]')) : 0
  );
  const [input12, setInput12] = useState(
    getValues('score[11]') ? Number(getValues('score[11]]')) : 0
  );

  const total =
    input1 +
    input2 +
    input3 +
    input4 +
    input5 +
    input6 +
    input7 +
    input8 +
    input9 +
    input10 +
    input11 +
    input12;

  return (
    <Box sx={{ width: 'max-content', margin: '50px auto' }}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <StyledTableCellWithoutLeftRight
              align="left"
              sx={{ padding: '16px' }}
            >
              问题
            </StyledTableCellWithoutLeftRight>
            <StyledTableCellWithoutLeftRight
              align="center"
              sx={{ padding: '16px' }}
            >
              评分
            </StyledTableCellWithoutLeftRight>
          </TableRow>
        </TableHead>
        <TableBody>
          {contentLabel.map(
            (
              content: {
                id: number;
                ko: string;
                desc: string[];
                scores: number[];
                ids: number[];
              },
              _
            ) => (
              <TableRow>
                <StyledTableCellWithoutLeftRight>
                  {content.desc.map((v, i) => {
                    return (
                      <TableRow
                        sx={{
                          height: '48px',
                          lineHeight: '48px',
                          borderBottom:
                            i !== content.desc.length - 1
                              ? '1px solid lightgray'
                              : '',
                        }}
                      >
                        <Box
                          sx={{
                            paddingLeft: '16px',
                            minWidth: '1100px',
                            fontSize: '14px',
                          }}
                        >
                          {v}
                          <br />
                          {v === '12. “请复制这个图案。"' && <MMSE9pic />}
                          {v === '1. 每个正确答案得 1 分' && (
                            <Box
                              sx={{
                                marginTop: '-13px',
                                marginBottom: '11px',
                              }}
                            >
                              {questions1.map(a => (
                                <Typography fontSize={'14px'}>• {a}</Typography>
                              ))}
                            </Box>
                          )}
                          {v === '2. 每个正确答案得 1 分' && (
                            <Box
                              sx={{ marginTop: '-15px', marginBottom: '9.5px' }}
                            >
                              {questions2.map(a => (
                                <Typography fontSize={'14px'}>• {a}</Typography>
                              ))}
                            </Box>
                          )}
                          {v ===
                            '3. 我会说三个词/物品，你需要重复它们。然后记住这些词，因为稍后我会让你再说一遍它们。' && (
                            <Typography
                              sx={{
                                fontSize: '14px',
                                margin: '-18px 0 10px 13px',
                              }}
                            >
                              示例: 球 - 车 - 男人 / 苹果 - 硬币 - 桌子{' '}
                            </Typography>
                          )}
                          {v ===
                            '10. "用你的右手/左手拿起这张纸，把它对折，然后放在地上。"' && (
                            <Typography
                              sx={{
                                fontSize: '14px',
                                margin: '-18px 0 10px 13px',
                              }}
                            >
                              (每完成一个动作得 1 分。)
                            </Typography>
                          )}
                        </Box>
                      </TableRow>
                    );
                  })}
                </StyledTableCellWithoutLeftRight>
                <StyledTableCellWithoutLeftRight>
                  {content.scores.map((score, i) => (
                    <TableRow
                      sx={{
                        height:
                          content.id === 6 && i === 1
                            ? '171.5px'
                            : (content.id === 1 && i === 0) ||
                              (content.id === 1 && i === 1)
                            ? '146px'
                            : (content.desc[2] && content.id === 1) ||
                              content.desc[5]
                            ? '58px'
                            : '48px',
                        lineHeight:
                          content.id === 6 && i === 1
                            ? '171.5px'
                            : (content.id === 1 && i === 0) ||
                              (content.id === 1 && i === 1)
                            ? '146px'
                            : (content.desc[2] && content.id === 1) ||
                              content.desc[5]
                            ? '58px'
                            : '48px',
                        textAlign: 'center',
                        borderBottom:
                          i !== content.desc.length - 1
                            ? '1px solid lightgray'
                            : '',
                      }}
                    >
                      <Box
                        sx={{
                          width: '120px',
                          height:
                            content.id === 6 && i === 1
                              ? '171.5px'
                              : (content.id === 1 && i === 0) ||
                                (content.id === 1 && i === 1)
                              ? '148px'
                              : (content.desc[2] && content.id === 1) ||
                                content.desc[5]
                              ? '58px'
                              : '48px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Form.MuiTextField
                          disabled={disabled}
                          required={false}
                          type="number"
                          inputProps={{ min, max }}
                          value={
                            content.desc[i].includes('1. Score')
                              ? input1
                              : content.desc[i].includes(String('2. Score'))
                              ? input2
                              : content.desc[i].includes(String('3.'))
                              ? input3
                              : content.desc[i].includes(String('4.'))
                              ? input4
                              : content.desc[i].includes(String('5.'))
                              ? input5
                              : content.desc[i].includes(String('6.'))
                              ? input6
                              : content.desc[i].includes(String('7.'))
                              ? input7
                              : content.desc[i].includes(String('8.'))
                              ? input8
                              : content.desc[i].includes(String('9.'))
                              ? input9
                              : content.desc[i].includes(String('10.'))
                              ? input10
                              : content.desc[i].includes(String('11.'))
                              ? input11
                              : content.desc[i].includes(String('12.'))
                              ? input12
                              : 0
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                /{score}
                              </InputAdornment>
                            ),
                          }}
                          {...register(`score[${content.ids[i]}]`, {
                            onChange: e => {
                              var value = parseInt(e.target.value, 10);
                              // console.log('✅', content.desc[i]);

                              let max = { score };

                              if (value >= max.score) value = max.score;
                              if (value < min) value = min;

                              if (content.desc[i].includes('1. Score'))
                                setInput1(value);
                              else if (content.desc[i].includes('2. Score'))
                                setInput2(value);
                              if (content.desc[i].includes('3.'))
                                setInput3(value);
                              if (content.desc[i].includes('4.'))
                                setInput4(value);
                              if (content.desc[i].includes('5.'))
                                setInput5(value);
                              if (content.desc[i].includes('6.'))
                                setInput6(value);
                              if (content.desc[i].includes('7.'))
                                setInput7(value);
                              if (content.desc[i].includes('8.'))
                                setInput8(value);
                              if (content.desc[i].includes('9.'))
                                setInput9(value);
                              if (content.desc[i].includes('10.'))
                                setInput10(value);
                              if (content.desc[i].includes('11.'))
                                setInput11(value);
                              if (content.desc[i].includes('12.'))
                                setInput12(value);
                            },
                          })}
                          // {...register(`score[${content.ids[i]}]`)}
                        />
                      </Box>
                    </TableRow>
                  ))}
                </StyledTableCellWithoutLeftRight>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-end'}
        sx={{ marginTop: '20px' }}
      >
        <Typography
          gutterBottom
          minWidth={115}
          fontWeight={700}
          variant="subtitle1"
        >
          总分 : {total}
        </Typography>
        <Typography
          minWidth={115}
          variant="caption"
          sx={{ color: `${palette.primary.main}` }}
        >
          {scoreLabel.map(({ score, label }) => (
            <Typography variant="inherit">
              <Box component={'strong'} mr={0.5}>
                {label} :
              </Box>
              {score}
            </Typography>
          ))}
        </Typography>
      </Box>
    </Box>
  );
};

export default MMSEContents;
